import { useState } from 'react';
import {
  Box, Button, ButtonBase, IconButton, MenuItem, Select, Stack, Typography, useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { formatDay } from '../../utils/dates';
import { isoDate } from '../../utils/format';
import { MAX_RANGE_DAYS, rangeDays } from './dateRanges';

const CELL = 36;
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = Array.from({ length: 12 }, (_, month) => new Date(2000, month, 1)
  .toLocaleDateString(undefined, { month: 'short' }));
const YEARS_BACK = 10;

// A month as a count of months, so they can be added to and compared
const toMonth = (iso) => Number(iso.slice(0, 4)) * 12 + Number(iso.slice(5, 7)) - 1;
const monthYear = (month) => Math.floor(month / 12);

// The days of a month, with a null for each blank before the 1st
function monthCells(month) {
  const year = monthYear(month);
  const index = month % 12;
  const blanks = new Date(year, index, 1).getDay();
  const days = new Date(year, index + 1, 0).getDate();
  return [...Array(blanks).fill(null),
    ...Array.from({ length: days }, (_, day) => isoDate(new Date(year, index, day + 1)))];
}

/**
 * Picks a custom range: the From and To days, then one or two months (one on small screens), where the first click
 * starts a range and the second ends it, either side of the first. Days after today, or too far from the start for
 * the API's limit, cannot be picked. onApply gets { since, until } (YYYY-MM-DD).
 */
export default function DateRangeCalendar({ since: initialSince, until: initialUntil, onApply, onBack }) {
  const theme = useTheme();
  const twoMonths = useMediaQuery(theme.breakpoints.up('sm'));
  const today = isoDate(new Date());
  const lastMonth = toMonth(today);
  const [range, setRange] = useState({ since: initialSince, until: initialUntil });
  const [hovered, setHovered] = useState(null);
  // The month on the right when there are two: the one the range ends in
  const [shown, setShown] = useState(toMonth(initialUntil));
  const { since, until } = range;

  const firstShown = twoMonths ? shown - 1 : shown;
  const months = twoMonths ? [shown - 1, shown] : [shown];
  const picking = since && !until;
  const end = picking ? hovered : until;
  const [low, high] = since && end && end < since ? [end, since] : [since, end];
  const tooLong = Boolean(since && until) && rangeDays(since, until) > MAX_RANGE_DAYS;

  const isDisabled = (day) => day > today || (picking && rangeDays(...[since, day].sort()) > MAX_RANGE_DAYS);
  const pick = (day) => {
    setHovered(null);
    setRange(picking ? { since: day < since ? day : since, until: day < since ? since : day }
      : { since: day, until: null });
  };
  // Shows the picked month and year; with two months, on the left unless that would pass the current month
  const jump = (month) => setShown(Math.min(twoMonths ? month + 1 : month, lastMonth));

  return (
    <Stack spacing={1.5} sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <DayBox label="From" value={since} active={!picking} />
        <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
        <DayBox label="To" value={until} active={Boolean(picking)} />
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <IconButton size="small" aria-label="Previous month" onClick={() => setShown(shown - 1)}
                    disabled={monthYear(firstShown - 1) < monthYear(lastMonth) - YEARS_BACK}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Stack direction="row" spacing={0.5}>
          <Select size="small" variant="standard" disableUnderline value={firstShown % 12} aria-label="Month"
                  onChange={(event) => jump(monthYear(firstShown) * 12 + event.target.value)}
                  sx={{ fontSize: 14, fontWeight: 600 }}>
            {MONTHS.map((name, month) => (
              <MenuItem key={name} value={month} dense
                        disabled={monthYear(firstShown) * 12 + month > lastMonth}>{name}</MenuItem>
            ))}
          </Select>
          <Select size="small" variant="standard" disableUnderline value={monthYear(firstShown)} aria-label="Year"
                  onChange={(event) => jump(event.target.value * 12 + (firstShown % 12))}
                  sx={{ fontSize: 14, fontWeight: 600 }}>
            {Array.from({ length: YEARS_BACK + 1 }, (_, back) => monthYear(lastMonth) - back).map((year) => (
              <MenuItem key={year} value={year} dense>{year}</MenuItem>
            ))}
          </Select>
        </Stack>
        <IconButton size="small" aria-label="Next month" onClick={() => setShown(shown + 1)}
                    disabled={shown >= lastMonth}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Stack direction="row" spacing={3} onMouseLeave={() => setHovered(null)}>
        {months.map((month) => (
          <Month key={month} month={month} low={low} high={high} today={today} isDisabled={isDisabled} onPick={pick}
                 onHover={picking ? setHovered : undefined} />
        ))}
      </Stack>

      {tooLong && (
        <Typography variant="caption" color="error">The range is limited to about 10 years.</Typography>
      )}
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button size="small" onClick={onBack}>Back</Button>
        <Button size="small" variant="contained" disabled={!since || !until || tooLong}
                onClick={() => onApply({ since, until })}>
          Apply
        </Button>
      </Stack>
    </Stack>
  );
}

function DayBox({ label, value, active }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0, px: 1.25, py: 0.5, borderRadius: 1.5, border: 1,
      borderColor: active ? 'primary.main' : 'divider' }}>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.3 }}>{label}</Typography>
      <Typography variant="body2" noWrap sx={{ fontWeight: 500, color: value ? 'text.primary' : 'text.secondary' }}>
        {value ? formatDay(value) : 'Pick a day'}
      </Typography>
    </Box>
  );
}

function Month({ month, low, high, today, isDisabled, onPick, onHover }) {
  const title = new Date(monthYear(month), month % 12, 1).toLocaleDateString(undefined,
    { month: 'long', year: 'numeric' });
  return (
    <Box sx={{ width: CELL * 7 }}>
      <Typography variant="subtitle2" align="center" sx={{ mb: 0.5 }}>{title}</Typography>
      <Box role="grid" aria-label={title} sx={{ display: 'grid', gridTemplateColumns: `repeat(7, ${CELL}px)` }}>
        {WEEKDAYS.map((name) => (
          <Typography key={name} variant="caption" align="center" color="text.secondary"
                      sx={{ lineHeight: `${CELL - 8}px` }}>
            {name}
          </Typography>
        ))}
        {monthCells(month).map((day, index) => (day ? (
          <Day key={day} day={day} low={low} high={high} today={today} disabled={isDisabled(day)} onPick={onPick}
               onHover={onHover} />
        // eslint-disable-next-line react/no-array-index-key
        ) : <Box key={`blank-${index}`} />))}
      </Box>
    </Box>
  );
}

function Day({ day, low, high, today, disabled, onPick, onHover }) {
  const isEnd = day === low || day === high;
  const inRange = Boolean(low && high) && day >= low && day <= high && low !== high;
  return (
    <Box sx={(theme) => ({
      height: CELL,
      // The band behind the days runs from the middle of the first day to the middle of the last
      background: inRange ? (() => {
        const band = alpha(theme.palette.primary.main, 0.1);
        if (day === low) return `linear-gradient(to right, transparent 50%, ${band} 50%)`;
        if (day === high) return `linear-gradient(to left, transparent 50%, ${band} 50%)`;
        return band;
      })() : 'transparent',
    })}>
      <ButtonBase disabled={disabled} onClick={() => onPick(day)} onMouseEnter={() => onHover?.(day)}
                  onFocus={() => onHover?.(day)} aria-label={formatDay(day)} aria-pressed={isEnd}
                  sx={{
                    width: CELL, height: CELL, borderRadius: '50%', fontSize: 13,
                    fontWeight: isEnd || day === today ? 600 : 400,
                    color: isEnd ? 'primary.contrastText' : disabled ? 'text.disabled' : 'text.primary',
                    bgcolor: isEnd ? 'primary.main' : 'transparent',
                    border: day === today && !isEnd ? 1 : 0, borderColor: 'primary.main',
                    '&:hover': { bgcolor: isEnd ? 'primary.dark' : 'action.hover' },
                    '&.Mui-focusVisible': { outline: 2, outlineColor: 'primary.main', outlineOffset: 1 },
                  }}>
        {Number(day.slice(8))}
      </ButtonBase>
    </Box>
  );
}
